import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-totp-login-card.css?inline';
import '../pk-card/pk-card.js';

/**
 * `<pk-totp-login-card>` — TOTP challenge-verify surface for the login flow.
 *
 * Different from `<pk-totp-setup-card>`: this is shown AFTER a user with TOTP
 * enrolled enters their password and is challenged for a 6-digit code. No QR /
 * manual-key UI — just lede + code field + submit + foot.
 *
 * Attributes:
 *   - `action`     — form POST URL (default: `/portal/totp/login`)
 *   - `error`      — error message; renders a `<pk-alert>` above the form
 *   - `csrf-token` — anti-forgery token; renders a hidden input
 *   - `csrf-name`  — CSRF hidden-input name (default `__RequestVerificationToken`)
 *   - `eyebrow`    — uppercase brand label. Default: `Two-factor auth`
 *   - `title`      — heading. Default: `Two-factor authentication`
 *   - `lede`       — paragraph beneath the heading. Default mentions entering the
 *                    6-digit code. Consumers can include the user's email via
 *                    Razor / template interpolation when relevant.
 *
 * Slots:
 *   - `foot` — links beneath the form ("Sign in with a different account")
 */
export class PkTotpLoginCard extends HTMLElement {
    static readonly tagName = 'pk-totp-login-card';

    static get observedAttributes(): string[] {
        return [
            'action', 'error',
            'csrf-token', 'csrf-name',
            'eyebrow', 'title', 'lede',
        ];
    }

    private readonly _root: ShadowRoot;
    private _submitting = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
        this._root.addEventListener('submit', this._handleSubmit);
    }

    disconnectedCallback(): void {
        this._root.removeEventListener('submit', this._handleSubmit);
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private readonly _handleSubmit = (e: Event): void => {
        const form = e.target as HTMLFormElement;
        if (!form.classList.contains('totp-login-form')) return;
        this._submitting = true;
        this.render();
    };

    private get _action(): string {
        return this.getAttribute('action') ?? '/portal/totp/login';
    }

    private get _error(): string {
        return this.getAttribute('error') ?? '';
    }

    private get _csrfToken(): string {
        return this.getAttribute('csrf-token') ?? '';
    }

    private get _csrfName(): string {
        return this.getAttribute('csrf-name') ?? '__RequestVerificationToken';
    }

    private get _eyebrow(): string {
        return this.getAttribute('eyebrow') ?? 'Two-factor auth';
    }

    private get _title(): string {
        return this.getAttribute('title') ?? 'Two-factor authentication';
    }

    private get _lede(): string {
        return this.getAttribute('lede')
            ?? 'Enter the 6-digit code from your authenticator app to continue signing in.';
    }

    private template(): TemplateResult {
        const action     = this._action;
        const error      = this._error;
        const csrfToken  = this._csrfToken;
        const csrfName   = this._csrfName;
        const eyebrow    = this._eyebrow;
        const heading    = this._title;
        const lede       = this._lede;
        const submitting = this._submitting;

        return html`
            <style>${styles}</style>
            <pk-card>
                <span slot="eyebrow" class="eyebrow">${eyebrow}</span>
                <h1>${heading}</h1>
                <p class="lede">${lede}</p>

                ${error ? html`
                    <pk-alert variant="error" class="form-error">${error}</pk-alert>
                ` : ''}

                <form
                    class="totp-login-form"
                    method="post"
                    action=${action}
                    novalidate
                >
                    ${csrfToken ? html`
                        <input type="hidden" name=${csrfName} .value=${csrfToken} />
                    ` : ''}

                    <pk-form-field
                        name="code"
                        type="text"
                        label="Verification code"
                        autocomplete="one-time-code"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        maxlength="6"
                        code
                        required
                        autofocus
                    ></pk-form-field>

                    <pk-button
                        variant="primary"
                        type="submit"
                        class="submit-btn"
                        ?data-submitting=${submitting}
                        loading-label="Verifying…"
                        ?disabled=${submitting}
                        aria-busy=${submitting ? 'true' : 'false'}
                    >Verify</pk-button>
                </form>

                <div slot="foot">
                    <slot name="foot">
                        <a href="/portal/login">Sign in with a different account</a>
                    </slot>
                </div>
            </pk-card>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkTotpLoginCard.tagName)) {
    customElements.define(PkTotpLoginCard.tagName, PkTotpLoginCard);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-totp-login-card': PkTotpLoginCard;
    }
}
